// wordingVocabulary.ts
import type { WordingIntent } from './wordingRules';

/**
 * Turn a rule's grade into the words THIS clinic uses.
 *
 * Every clinic keeps its own phrase list (`dropdown_settings`, surfaced by
 * `useClinicDropdowns`), and the lists genuinely differ — one writes "Mildly dilated",
 * another "mildly reduced", and the right-ventricle options are written to continue the
 * sentence ("with mildly reduced systolic function"). Some carry typos that a clinician
 * has lived with for a year.
 *
 * So the engine never writes a phrase of its own. It matches the grade against the
 * clinic's own options and, if nothing matches, writes nothing at all. A sentence in a
 * signed report should read in the clinic's voice or be left alone.
 */

/**
 * Candidate wordings per grade, most specific first. These are matched AGAINST a clinic's
 * list — they are never inserted directly.
 */
const INTENT_SYNONYMS: Record<WordingIntent, string[]> = {
  normal: ['normal'],
  mild: ['mildly reduced', 'mildly dilated', 'mild', 'grade i'],
  moderate: ['moderately reduced', 'moderately dilated', 'moderate', 'grade ii'],
  severe: ['severely reduced', 'severely dilated', 'severe', 'grade iii'],
  elevated: ['elevated', 'raised', 'increased'],
};

const normalize = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:]+$/, '');

/**
 * The clinic's own wording for `intent`, or null when its list cannot express this grade.
 *
 * Matching is exact-first, then "starts with", then "contains" — so "mildly reduced" wins
 * over a list entry that merely contains the word "mild", and a list written to continue a
 * sentence ("with mildly reduced systolic function") still resolves.
 */
export function resolvePhrase(intent: WordingIntent, options: string[] | undefined): string | null {
  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }
  const candidates = INTENT_SYNONYMS[intent] || [];

  for (const candidate of candidates) {
    const exact = options.find(option => normalize(option) === candidate);
    if (exact) {
      return exact;
    }
  }
  for (const candidate of candidates) {
    const startsWith = options.find(option => normalize(option).startsWith(candidate));
    if (startsWith) {
      return startsWith;
    }
  }
  for (const candidate of candidates) {
    // Guard against the shortest synonyms ("mild") matching an unrelated sentence-long
    // option; only the multi-word forms are safe to match loosely.
    if (candidate.length < 6) {
      continue;
    }
    const contains = options.find(option => normalize(option).includes(candidate));
    if (contains) {
      return contains;
    }
  }
  return null;
}
